import JSON5 from 'json5';
import {toast} from 'react-toastify';

import YAML from '../../../../services/yaml';
import {TemplateVariable} from '../../../generated/graphql';

export type TemplateVariableType = 'string' | 'integer' | 'number';

export interface VariableFormState {
  name: string;
  description?: string;
  type?: TemplateVariableType;
  values?: string;
  minimum?: number;
  maximum?: number;
  default?: string;
}

export type TemplateVariableInput = {
  schema: Record<string, any>;
  description?: string;
  default?: string | number;
};

const SINGLE_PLACEHOLDER_PATTERN = /^\{\{(\w+)\}\}$/;

const parseEnumValuesInput = (input: string, name: string) => {
  let values: any;
  if (input.length === 0) {
    return [];
  }
  const isQuoted = input.includes('"') || input.includes("'");
  try {
    values = isQuoted
      ? JSON5.parse('[' + input + ']')
      : input.split(',').map(value => value.trim());
  } catch (error) {
    throw new Error(
      `Given values for template variable ${name} are invalid: ${error}`
    );
  }
  if (
    values.some(
      (value: any) => typeof value !== 'string' && typeof value !== 'number'
    )
  ) {
    throw new Error(
      `All values for template variable ${name} must be either numbers or strings.`
    );
  }
  return values;
};

// // Helper that converts the form state to the json format that the backend
// // expects.
export const JsonFromFormState = (
  name: string,
  state: VariableFormState
): TemplateVariableInput | undefined => {
  /* eslint-disable no-case-declarations */
  // FIXME: There are a few variables assigned in `case` statements that
  //        should be reworked. Ignore the issue here rather than fix and
  //        potentially introduce new bugs.
  switch (state.type?.toLocaleLowerCase() ?? '') {
    case 'string':
      return {
        ...(state.description && {description: state.description}),
        schema: {
          type: state.type,
          ...(state.default && {default: state.default}),
          ...(state.values != null &&
            state.values.length > 0 && {
              // we want users to input comma separated values, but if their value
              // would contain a comma, we support this by having them wrap values in
              // quotes then use json parse to get the intended value
              // JSON5 allows for any quote type
              enum: parseEnumValuesInput(state.values, state.name),
            }),
        },
      };
    case 'integer':
      if (state.minimum != null && !Number.isInteger(state.minimum)) {
        throw new Error('Minimum must be an integer for variable ' + name);
      }
      if (state.maximum != null && !Number.isInteger(state.maximum)) {
        throw new Error('Maximum must be an integer for variable ' + name);
      }
      if (
        state.minimum != null &&
        state.maximum != null &&
        state.minimum > state.maximum
      ) {
        throw new Error(
          'Minimum must be less than or equal to maximum for variable ' + name
        );
      }
      const defaultValueNumber = parseInt(state.default || '', 10);
      if (state.default && !Number.isInteger(defaultValueNumber)) {
        throw new Error(
          'Default value must be an integer for variable ' + name
        );
      }
      return {
        ...(state.description && {description: state.description}),
        schema: {
          type: state.type,
          ...(state.default && {default: defaultValueNumber}),
          ...(state.minimum != null && {minimum: state.minimum}),
          ...(state.maximum != null && {maximum: state.maximum}),
        },
      };
    case 'number':
      if (
        state.minimum != null &&
        state.maximum != null &&
        state.minimum > state.maximum
      ) {
        throw new Error(
          'Minimum must be less or equal to maximum for variable ' + name
        );
      }
      const defaultValueFloat = state.default
        ? parseFloat(state.default)
        : null;
      if (state.default && !Number.isFinite(defaultValueFloat)) {
        throw new Error('Default value must be a number for variable ' + name);
      }
      return {
        ...(state.description && {description: state.description}),
        schema: {
          type: state.type,
          ...(state.default && {default: defaultValueFloat}),
          ...(state.minimum != null && {minimum: state.minimum}),
          ...(state.maximum != null && {maximum: state.maximum}),
        },
      };
    // case undefined:
    //   toast.error('Variable type is required for variable ' + name);
    //   return undefined;
    default:
      toast.error(
        'Invalid variable type for variable ' +
          name +
          '. Please configure the variable.'
      );
      return undefined;
  }
};

export const FormStateFromGqlVariable = (
  variable: TemplateVariable
): VariableFormState => {
  const schema = JSON.parse(variable.schema);
  return {
    name: variable.name,
    description: variable?.description ?? undefined,
    type: schema.type,
    values: schema.enum?.join(', '),
    minimum: schema.minimum != null ? parseFloat(schema.minimum) : undefined,
    maximum: schema.maximum != null ? parseFloat(schema.maximum) : undefined,
    default: schema.default,
  };
};

export function sortTemplateVariablesByConfig<
  T extends TemplateVariable[] | VariableFormState[],
>(config: string, templateVariables: T): T {
  const variableNames = [...config.matchAll(/\{\{(\w+)\}\}/g)].map(
    match => match[1]
  );
  return templateVariables.sort((a, b) => {
    const indexOfA = variableNames.indexOf(a.name);
    const indexOfB = variableNames.indexOf(b.name);
    if (indexOfA === -1) {
      return 1;
    }
    if (indexOfB === -1) {
      return -1;
    }

    return indexOfA - indexOfB;
  }) as T;
}

export const isSingleVarString = (str: string) => {
  return SINGLE_PLACEHOLDER_PATTERN.test(str);
};

function preprocessYaml(yamlStr: string): {
  preprocessedYaml: string;
  placeholders: {[key: string]: string};
} {
  const placeholders: {[key: string]: string} = {};
  let index = 0;

  const preprocessedYaml = yamlStr.replace(/{{[^{}]+}}/g, match => {
    const placeholder = `__HANDLEBARS_EXPR_${index++}__`;
    placeholders[placeholder] = match;
    return placeholder;
  });

  return {preprocessedYaml, placeholders};
}

// Function to restore Handlebars expressions in the object
function postprocessJson(obj: any, placeholders: {[key: string]: string}) {
  const jsonStr = JSON.stringify(obj);

  return JSON.parse(
    jsonStr.replace(/__HANDLEBARS_EXPR_\d+__/g, match => {
      return placeholders[match];
    })
  );
}

export function ParseYamlWithHandlebarsToJson(configValue: string) {
  const {preprocessedYaml, placeholders} = preprocessYaml(configValue);
  const obj = YAML.parse(preprocessedYaml);
  // Restore Handlebars expressions in the JSON object
  return postprocessJson(obj, placeholders);
}

function serializeToJson(obj: any) {
  return YAML.stringify(obj);
}
// Post-process to unquote Handlebars expressions
function postProcessYaml(yamlStr: string) {
  // This regex looks for quoted strings that contain Handlebars expressions and removes the quotes
  return yamlStr.replace(
    // eslint-disable-next-line no-useless-backreference
    /'(({{[^}]+}}[^']*)+)'|"(?:(?=(\\?))\2.)*?"/g,
    (match, p1) => {
      if (p1) {
        // If it's a single-quoted string with Handlebars, remove the single quotes
        return p1;
      } else {
        // Otherwise, replace with the matched string without changes
        return match;
      }
    }
  );
}

export function JsonToYamlWithHandlebars(obj: any) {
  const yamlString = serializeToJson(obj);
  return postProcessYaml(yamlString);
}
